import { Link, useLocation, useNavigate } from "react-router-dom";
import { Instagram, Send, Facebook, MapPin, Mail } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { SITE, scrollToId } from "@/config";

export default function Footer() {
  const { lang, t } = useLanguage();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const goAnchor = (hash) => {
    if (pathname !== `/${lang}`) navigate(`/${lang}#${hash}`);
    else scrollToId(hash);
  };
  return (
    <footer className="bg-ink text-cream/90" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-3 gap-12">
        <div>
          <p className="font-serif text-xl font-semibold mb-1">Tatiana Nakryzhskaya</p>
          <p className="text-xs font-mono uppercase tracking-[0.18em] text-cream/50 mb-4">MD, PhD, CFMP</p>
          <p className="text-sm text-cream/60 max-w-xs">{t.footer.tagline}</p>
          <div className="flex gap-3 mt-6">
            {SITE.socials.instagram && (
            <a href={SITE.socials.instagram} target="_blank" rel="noreferrer" data-testid="footer-social-instagram" className="w-9 h-9 rounded-full border border-cream/20 flex items-center justify-center hover:bg-terra hover:border-terra transition-colors" aria-label="Instagram">
              <Instagram className="w-4 h-4" />
            </a>
            )}
            {SITE.socials.telegram && (
            <a href={SITE.socials.telegram} target="_blank" rel="noreferrer" data-testid="footer-social-telegram" className="w-9 h-9 rounded-full border border-cream/20 flex items-center justify-center hover:bg-terra hover:border-terra transition-colors" aria-label="Telegram">
              <Send className="w-4 h-4" />
            </a>
            )}
            {SITE.socials.facebook && (
            <a href={SITE.socials.facebook} target="_blank" rel="noreferrer" data-testid="footer-social-facebook" className="w-9 h-9 rounded-full border border-cream/20 flex items-center justify-center hover:bg-terra hover:border-terra transition-colors" aria-label="Facebook">
              <Facebook className="w-4 h-4" />
            </a>
            )}
          </div>
        </div>
        <div>
          <h4 className="text-xs font-mono uppercase tracking-[0.2em] text-cream/50 mb-4">{t.footer.nav_title}</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to={`/${lang}/about`} className="hover:text-terra transition-colors" data-testid="footer-link-about">{t.nav.about}</Link></li>
            <li><button onClick={() => goAnchor("patients")} className="hover:text-terra transition-colors" data-testid="footer-link-patients">{t.nav.patients}</button></li>
            <li><button onClick={() => goAnchor("directions")} className="hover:text-terra transition-colors" data-testid="footer-link-directions">{t.nav.directions}</button></li>
            <li><button onClick={() => goAnchor("approach")} className="hover:text-terra transition-colors" data-testid="footer-link-approach">{t.nav.approach}</button></li>
            <li><Link to={`/${lang}/blog`} className="hover:text-terra transition-colors" data-testid="footer-link-blog">{t.nav.blog}</Link></li>
            <li><Link to={`/${lang}/contacts`} className="hover:text-terra transition-colors" data-testid="footer-link-contacts">{t.nav.contacts}</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-xs font-mono uppercase tracking-[0.2em] text-cream/50 mb-4">{t.footer.contact_title}</h4>
          <p className="text-sm text-cream/80 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-terra shrink-0" />
            <span data-testid="footer-city">{SITE.city[lang]}</span>
          </p>
          {SITE.email && (
            <p className="text-sm text-cream/80 flex items-center gap-2 mt-2">
              <Mail className="w-4 h-4 text-terra shrink-0" />
              <a href={`mailto:${SITE.email}`} className="hover:text-terra" data-testid="footer-email">{SITE.email}</a>
            </p>
          )}
          <p className="text-sm text-cream/60 mt-2">{t.contacts.online}</p>
        </div>
      </div>
      <div className="border-t border-cream/10 py-6 text-center text-xs text-cream/40 space-y-1 px-4">
        <p>{t.footer.rights}</p>
        <p>{t.footer.note}</p>
        <p className="flex items-center justify-center gap-4 flex-wrap">
          <Link to={`/${lang}/privacy`} className="underline underline-offset-2 hover:text-terra transition-colors" data-testid="footer-privacy-link">
            {t.privacy.link}
          </Link>
          <button onClick={() => window.dispatchEvent(new Event("open-cookie-settings"))} className="underline underline-offset-2 hover:text-terra transition-colors" data-testid="footer-cookie-settings">
            {t.footer.cookie_settings}
          </button>
        </p>
      </div>
    </footer>
  );
}
